import * as ADGroups from '../constants/ADGroups';
import * as JobCodes from '../constants/JobCodes';
import EmployeeInfo from "../types/Employee/EmployeeInfo";

/**
 * Pattern: job-code primitives → feature access functions → page-level permission checks.
 *
 * Job-code primitives (reliabilityEngineerAccess, developerAccess, etc.) are the single
 * source of truth for which job codes belong to a role.  Feature functions (userCanAdd*,
 * userCanEdit*, userCanView*) compose those primitives into meaningful capabilities, keeping
 * each feature's access rules in one place.  Page-level checks (hasTrainingTabAccess,
 * hasQualityAdminAccess, etc.) combine feature functions to answer broader "can this user
 * see this section at all?" questions.
 *
 * Add/Edit distinctions are kept as separate functions even when they currently share the
 * same rule set — this makes it easy to diverge them later without hunting for call sites.
 * Functions that intentionally return true/false unconditionally are stubs: the permission
 * boundary exists in the architecture even when the rule hasn't been locked down yet.
 */

export const isTest = (process.env.REACT_APP_IS_TEST ?? '') === 'true';

export const isDev = (process.env.REACT_APP_IS_DEV ?? '') === 'true';

export const isAvailableNCRDay = (epoch: number = new Date().getTime()) => {
    const startDayEpoch = new Date(2024, 3, 8).getTime(); // year, month - 1, day
    const endDayEpoch = new Date(2024, 3, 18).getTime(); // year, month - 1, date
    // access is granted for a specified date range
    return (epoch > startDayEpoch && epoch < endDayEpoch);
};

/** One-off NCR access: open to RE job codes or anyone in a test environment, but only during the designated date window. */
export const checkIfOneOffNCR = (user: EmployeeInfo) => {
    return (isTest || reliabilityEngineerAccess(user.jobCode))
    && isAvailableNCRDay();
};

/** Rule editor access: RE job codes in any environment; developers in test only. */
export const hasRuleEditorAccess = (user: EmployeeInfo) => (
    (reliabilityEngineerAccess(user.jobCode)
    || (developerAccess(user.jobCode) && isTest))
);

// ─── Job-code primitives ──────────────────────────────────────────────────────
// These are the single source of truth for role membership.  Compose them in
// feature-level functions below rather than repeating job-code lists elsewhere.

export const reliabilityEngineerAccess = (jobCode: string) => {
    const reJobCodeList = [
        JobCodes.ASSOCIATE_RELIABILITY_ENGINEER,
        JobCodes.RELIABILITY_ENGINEER,
        JobCodes.MANAGER_I_RELIABILITY_ENGINEER
    ];
    return(reJobCodeList.includes(jobCode));
};

export const developerAccess = (jobCode: string) => {
    const developerJobCodeList = [
        JobCodes.ASSOCIATE_APPLICATION_DEVELOPER,
        JobCodes.APPLICATION_DEVELOPER,
        JobCodes.SENIOR_APPLICATION_DEVELOPER
    ];
    return(developerJobCodeList.includes(jobCode));
};

export const qualityAdminAccess = (jobCode: string) => {
    const qualityAdminJobCodeList = [
        JobCodes.QUALITY_SPECIALIST,
        JobCodes.QUALITY_DATA_ANALYST,
        JobCodes.QUALITY_SYSTEMS_ANALYST,
        JobCodes.SENIOR_QUALITY_SYSTEMS_ANALYST,
        JobCodes.QUALITY_SUPERINTENDENT,
        JobCodes.DIRECTOR_QUALITY,
        JobCodes.QUALITY_CONTROL_MANAGER,
        JobCodes.PROJECT_CONSTRUCTION_COORDINATOR
    ];
    return(qualityAdminJobCodeList.includes(jobCode));
};

export const businessSystemAnalystAccess = (jobCode: string) => {
    const businessSystemAnalystJobCodeList = [
        JobCodes.ASSOCIATE_BUSINESS_SYSTEM_ANALYST,
        JobCodes.BUSINESS_SYSTEM_ANALYST,
        JobCodes.SENIOR_BUSINESS_SYSTEM_ANALYST
    ];
    return(businessSystemAnalystJobCodeList.includes(jobCode))
};

/** Not exported — used only to compose broader feature checks below. */
const ITBusinessPartnerAccess = (jobCode: string) => {
    const ITBPJobCodeList = [
        JobCodes.IT_BUSINESS_PARTNER,
    ];
    return(ITBPJobCodeList.includes(jobCode));
};

/** Not exported — used only to compose broader feature checks below. */
const isWalkMeAccess = (jobCode: string) => {
    const walkMeJobCodeList = [
        JobCodes.SENIOR_HRIS_ANALYST,
    ];
    return(walkMeJobCodeList.includes(jobCode));
};

// ─── Page / section access ────────────────────────────────────────────────────

/** Controls the "mock as another user" feature in the navbar. */
export const hasMockUserAccess = (user: EmployeeInfo) => (
    reliabilityEngineerAccess(user.jobCode)
    || (developerAccess(user.jobCode) && isTest)
    || (businessSystemAnalystAccess(user.jobCode) && isTest)
);

/** Controls visibility of the admin section. */
export const hasAnyAdminAccess = (user: EmployeeInfo) => (
    reliabilityEngineerAccess(user.jobCode)
    || (developerAccess(user.jobCode) && isTest)
);

/** Controls visibility of the quality admin section. */
export const hasQualityAdminAccess = ({ jobCode }: EmployeeInfo) => (
    reliabilityEngineerAccess(jobCode)
    || qualityAdminAccess(jobCode)
    || (developerAccess(jobCode) && isTest)
    || (businessSystemAnalystAccess(jobCode) && isTest)
    || (ITBusinessPartnerAccess(jobCode) && isTest)
    || (isWalkMeAccess(jobCode) && isTest)
);

/**
 * Controls visibility of the Training tab, and gates its three routes in App.tsx.
 * Shown when the user can access at least one training sub-page — the sub-page
 * permissions are the only restriction, so the tab is available in every environment.
 */
export const hasTrainingTabAccess = (userData: EmployeeInfo) => (
    userCanViewStandardWorkTrainer(userData)
    || userCanViewWorkdayTopics(userData)
    || userCanViewWorkdayCertifications(userData)
);

// ─── Standard Work Trainer permissions ───────────────────────────────────────

/** Intentional stub — view access is currently open to all users. */
export const userCanViewStandardWorkTrainer = (_userData: EmployeeInfo) => {
    return true;
};

/** Controls the "+ New" batch certification button. */
export const userCanAddStandardWorkTrainer = (userData: EmployeeInfo) => {
    const { jobCode } = userData;
    return (
        jobCode === JobCodes.DIRECTOR_FIELD_OPERATIONS
        || jobCode === JobCodes.GENERAL_SUPERINTENDENT
        || jobCode === JobCodes.FIELD_PROCESS_IMPROVEMENT_SPECIALIST
        || jobCode === JobCodes.ASSOCIATE_FIELD_PROCESS_IMPROVEMENT_ANALYST
        || jobCode === JobCodes.FIELD_PROCESS_IMPROVEMENT_ANALYST
        || jobCode === JobCodes.SENIOR_FIELD_PROCESS_IMPROVEMENT_ANALYST
        || jobCode === JobCodes.MANAGER_I_FIELD_PROCESS_IMPROVEMENT
        || jobCode === JobCodes.SENIOR_MANAGER_PROCESS_IMPROVEMENT
        || reliabilityEngineerAccess(jobCode)
        || (developerAccess(jobCode) && isTest)
        || (businessSystemAnalystAccess(jobCode) && isTest)
        || (ITBusinessPartnerAccess(jobCode) && isTest)
        || (isWalkMeAccess(jobCode) && isTest)
    );
};

/**
 * Controls the "Edit Certificates" button on individual employee rows.
 * Kept separate from userCanAddStandardWorkTrainer so add and edit access
 * can be granted independently as requirements evolve.
 */
export const userCanEditStandardWorkTrainer = (userData: EmployeeInfo) => (
    userCanAddStandardWorkTrainer(userData)
);

// ─── Workday Topics permissions ───────────────────────────────────────────────

/** Intentional stub — view access is currently open to all users. */
export const userCanViewWorkdayTopics = (_userData: EmployeeInfo) => {
    return true;
};

// ─── Workday Certifications permissions ───────────────────────────────────────

/** Intentional stub — view access is currently open to all users. */
export const userCanViewWorkdayCertifications = (_userData: EmployeeInfo) => {
    return true;
};

/** Controls edit actions on the Workday Certifications page. */
export const userCanEditWorkdayCertifications = (userData: EmployeeInfo) => (
    userCanAddStandardWorkTrainer(userData)
);

// ─── Benchmark Creation Goal permissions ──────────────────────────────────────

/**
 * Controls edit actions on the Benchmark Creation Goal page.
 * Kept separate from userCanAddBenchmarkCreationGoal so add and edit access
 * can be granted independently as requirements evolve.
 */
export const userCanEditBenchmarkCreationGoal = (userData: EmployeeInfo) => (
    userCanAddBenchmarkCreationGoal(userData)
);

/** Controls add actions on the Benchmark Creation Goal page. */
export const userCanAddBenchmarkCreationGoal = ({ jobCode }: EmployeeInfo) => {
    return(
        jobCode === JobCodes.QUALITY_SPECIALIST
        || jobCode === JobCodes.QUALITY_SYSTEMS_ANALYST
        || jobCode === JobCodes.SENIOR_QUALITY_SYSTEMS_ANALYST
        || jobCode === JobCodes.QUALITY_SUPERINTENDENT
        || jobCode === JobCodes.QUALITY_CONTROL_MANAGER
        || jobCode === JobCodes.PROJECT_CONSTRUCTION_COORDINATOR
        || (developerAccess(jobCode) && isTest)
        || (reliabilityEngineerAccess(jobCode) && isTest)
        || (businessSystemAnalystAccess(jobCode) && isTest)
        || (ITBusinessPartnerAccess(jobCode) && isTest)
    );
}

// ─── Quality Control permissions ──────────────────────────────────────────────

/** Controls edit actions on the Quality Control page. */
export const userCanEditQualityControlGoals = (user: EmployeeInfo): boolean => {
    const adGroupIds = user.adGroups.map(x => x.id);
    return (
        adGroupIds.includes(ADGroups.QUALITY_SUPT)
        || adGroupIds.includes(ADGroups.QUALITY_SYSTEM_ANALYSTS)
        || user.jobCode === JobCodes.QUALITY_CONTROL_MANAGER
        || user.jobCode === JobCodes.PROJECT_CONSTRUCTION_COORDINATOR
    );
};